import { useMutation } from "@tanstack/react-query";
import { isAxiosError, isCancel } from "axios";
import { apiClient } from "../../../zodios/client";

const POLL_INTERVAL_MS = 2000;
// Matches the backend's SIMULATION_RUNNING_TTL_SECONDS, after which an unfinished run is treated as lost.
const MAX_WAIT_MS = 15 * 60 * 1000;

export class BidSimulationFailedError extends Error {}

type SimulateBidsVars = Parameters<typeof apiClient.simulateBids>[0] & { signal?: AbortSignal };

async function getSimulationStatus(simulationId: string, signal?: AbortSignal) {
  try {
    return await apiClient.getBidSimulation({ params: { simulation_id: simulationId }, signal });
  } catch (error) {
    // Keep polling through gateway and network errors; a 4xx (404 for a lost run) is final.
    if (isAxiosError(error) && !isCancel(error) && (error.response?.status ?? 500) >= 500) return undefined;
    throw error;
  }
}

export function useSimulateBids() {
  return useMutation({
    mutationFn: async ({ signal, ...body }: SimulateBidsVars) => {
      const { simulation_id } = await apiClient.simulateBids(body, { signal });
      const deadline = Date.now() + MAX_WAIT_MS;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        signal?.throwIfAborted();
        const simulation = await getSimulationStatus(simulation_id, signal);
        if (simulation?.status === "done") return simulation.result;
        if (simulation?.status === "failed") throw new BidSimulationFailedError(simulation.error);
      }
      throw new Error("Bid simulation did not finish in time");
    },
    meta: {
      expectedError: (error: unknown) =>
        error instanceof BidSimulationFailedError ||
        isCancel(error) ||
        (error instanceof DOMException && error.name === "AbortError") ||
        (isAxiosError(error) && error.response?.status === 422),
    },
  });
}
